import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { callAPI } from '../../../../services/api.service';

// Post-deploy smoke test for the order-fulfillment chain: ShipStation, the N8N dispatch webhook,
// the PDF zipper Lambda and the callback URL they answer on. Every probe is read-only — running
// this never places an order or triggers a print job. Server: /admin/fulfillment-connectivity.

type CheckStatus = 'ok' | 'warn' | 'fail' | 'skipped';

interface ConnectivityCheck {
  key: string;
  label: string;
  status: CheckStatus;
  detail: string;
  hint?: string;
  ms: number;
}

const STATUS_STYLE: Record<CheckStatus, { chip: string; text: string; row: string }> = {
  ok: { chip: 'bg-green-100 text-green-800', text: 'Connected', row: 'border-green-200' },
  warn: { chip: 'bg-amber-100 text-amber-900', text: 'Needs attention', row: 'border-amber-200' },
  fail: { chip: 'bg-red-100 text-red-800', text: 'Failing', row: 'border-red-200' },
  skipped: { chip: 'bg-gray-100 text-gray-600', text: 'Not configured', row: 'border-gray-200' },
};

const FulfillmentConnectivity: React.FC = () => {
  const [checks, setChecks] = useState<ConnectivityCheck[] | null>(null);
  const [environment, setEnvironment] = useState<string>('');
  const [ranAt, setRanAt] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const runChecks = async () => {
    setLoading(true);
    try {
      const response = await callAPI('admin/fulfillment-connectivity/check', null, 'GET');
      if (response?.checks) {
        setChecks(response.checks);
        setEnvironment(response.environment || '');
        setRanAt(response.ranAt || '');
        const failing = response.checks.filter((check: ConnectivityCheck) => check.status === 'fail').length;
        failing
          ? toast.error(`${failing} check${failing === 1 ? '' : 's'} failing`)
          : toast.success('Fulfillment chain reachable');
      } else {
        toast.error(response?.error || 'Could not run the checks');
      }
    } catch {
      toast.error('Could not run the checks');
    } finally {
      setLoading(false);
    }
  };

  const failing = (checks || []).filter((check) => check.status === 'fail').length;
  const attention = (checks || []).filter((check) => check.status === 'warn').length;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h2 className="text-lg font-semibold">Fulfillment connectivity</h2>
        <button
          type="button"
          onClick={runChecks}
          disabled={loading}
          className="bg-storyblue text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Run checks'}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Verifies the three services an order depends on — ShipStation, the N8N print-dispatch webhook and the PDF
        zipper Lambda — plus the callback URL they answer back on. Read-only: nothing is ordered, printed or emailed,
        so it is safe to run on production after a deploy.
      </p>

      {checks && (
        <>
          <div className="text-sm text-gray-600 mb-3">
            {failing === 0 && attention === 0
              ? 'All checks passed.'
              : `${failing} failing · ${attention} needing attention`}
            {environment && <span className="text-gray-400"> — environment: {environment}</span>}
            {ranAt && <span className="text-gray-400"> · {new Date(ranAt).toLocaleTimeString()}</span>}
          </div>
          <div className="flex flex-col gap-2">
            {checks.map((check) => {
              const style = STATUS_STYLE[check.status] || STATUS_STYLE.skipped;
              return (
                <div key={check.key} className={`border rounded-lg px-4 py-3 ${style.row}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-sm">{check.label}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${style.chip}`}>{style.text}</span>
                    <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">{check.ms} ms</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">{check.detail}</div>
                  {check.hint && <div className="text-sm text-gray-500 mt-1 italic">{check.hint}</div>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default FulfillmentConnectivity;
